import { observer } from "mobx-react-lite";
import { CallToAction, Message } from "core/services/inbox.service";
import { Button } from "components/common-ui";
import { useNavigate, useParams } from "react-router-dom";
import { useEffectOnce } from "@lib-shared/hooks";
import { useContext } from "react";
import { BusinessPage, dateWrapper, SelfServiceContext } from "core";
import useInboxLogic from "./use-inbox-logic";
import getAnalyticTags from "@lib-shared/lib/get-analytics-tags";

const getAnalytics = (message: Message, text?: string) => {
    return getAnalyticTags({
        component_name: "my_messages",
        component_type: "button",
        section_name: message.content.title,
        cta_text: text ?? `${message.content.callToActionTitle}`,
        target_page_url: location.href
    });
}
const getDeleteAnalytics = (message: Message) => {
    return getAnalyticTags({
        component_name: "my_messages",
        component_type: "button",
        section_name: message.content.title,
        cta_text: "Delete",
        target_page_url: location.href
    });
}

type ViewMessageProps = {
    message: Message;
    gotoInbox: () => void;
}

//TODO: Moveout
const staticFiles = ["pdf", "gif", "jpeg", "jpg", "png", "svg", "webp", "docx", "xlsx", "txt", "ppt", "pptx", "mp4", "mp3", "mp4", "zip", "7z"];

const isDownloadable = (callToAction: CallToAction) => {
    const url = callToAction?.link?.toLowerCase() ?? "";
    const index = url.lastIndexOf(".");

    if (index == -1) {
        return false;
    }

    const extension = url.substring(index + 1);
    return staticFiles.includes(extension);
}

const isExternalLink = (callToAction: CallToAction) => {
    const url = callToAction?.link?.toLowerCase() ?? "";

    if (!url) {
        return false;
    }

    return !url.startsWith(window.location.hostname);
}

const openInNewTab = (callToAction: CallToAction) => {
    return isDownloadable(callToAction) || isExternalLink(callToAction);
}

const DisplayMessage = (props: ViewMessageProps) => {
    const { message, gotoInbox } = props;
    const { category, content, date } = message;
    const { details, title } = content;
    const { callToAction } = details;
    const { deleteMessageAsync, markAsReadAsync } = useInboxLogic();
    const dw = dateWrapper(date);

    //const handleCallToAction = () => window.location.replace(callToAction.link);

    useEffectOnce(() => {
        if (message.isRead) {
            return;
        }

        const markAsRead = async () => await markAsReadAsync(message);
        markAsRead();
    });

    const openInNew = openInNewTab(callToAction);

    return <section className="message">
        <header>
            <h5>{title}</h5>

            <span>
                <time>
                    {dw.dayShortMonthAndFullYear} {dw.time}
                </time>
                <button type="button"
                    {...getDeleteAnalytics(message)}
                    onClick={() => deleteMessageAsync(message, gotoInbox)}>
                    <i className="icon-imi-delete" />
                </button>
            </span>
        </header>

        <img src={details.media} alt="image" />

        <span className="category">
            {category.title}
        </span>

        <p>
            {details.content}
        </p>

        <footer>
            <Button
                text="Back to Messages"
                colour="outline-black"
                {...getAnalytics(message, "Back to Messages")}
                onClick={gotoInbox} />
            {/* {!!callToAction.title
                && <Button text={callToAction.title}
                    colour="primary"
                    onClick={handleCallToAction} />} */}
            {!!callToAction.title
                && <a
                    className="btn btn-primary"
                    target={openInNew ? "_blank" : "_self"}
                    //download={isDownloadable(callToAction)}
                    href={callToAction.link}>{callToAction.title}</a>}
        </footer>
    </section>;
}

const ViewMessage = observer(() => {
    const { id } = useParams();
    const selfServiceContext = useContext(SelfServiceContext);
    const store = selfServiceContext.stores.inbox;
    const navigate = useNavigate();

    const gotoInbox = () => {
        store.updateSelectedMessage(undefined);
        navigate(BusinessPage.inbox);
    }

    useEffectOnce(() => {
        const load = async () => {
            if (!store.hasLoaded) {
                await selfServiceContext.managers.inbox.populateAsync();
            }

            const message = store.messages.find(m => m.id === id);
            store.updateSelectedMessage(message);
        }

        load();
    });

    return <>
        {!!store.selectedMessage && <DisplayMessage message={store.selectedMessage} gotoInbox={gotoInbox} />}
    </>;
});

export default ViewMessage;
